import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Images, Plus, Upload, Trash2, CheckCircle2, Archive, X, Loader2,
    Bike, Tag, Info, AlertTriangle, FolderOpen, Search as SearchIcon, ChevronDown,
    Wand2, Eraser, Building2, Sparkles, Share2, Download,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const COND_OPTIONS = [
    { key: "neu", label: "Neufahrzeug", shared: true },
    { key: "tageszulassung", label: "Tageszulassung", shared: true },
    { key: "vorfuehr", label: "Vorführfahrzeug", shared: false },
    { key: "gebraucht", label: "Gebraucht", shared: false },
];
const COND_LABEL = Object.fromEntries(COND_OPTIONS.map((c) => [c.key, c.label]));
const STATUS_LABEL = { approved: "Freigegeben", review: "Prüfung", archived: "Archiviert" };
const SHARE_OPTIONS = [
    { key: "none", label: "Nicht teilen" },
    { key: "branches", label: "Eigene Filialen" },
    { key: "all", label: "Alle Händler" },
];
const SHARE_LABEL = Object.fromEntries(SHARE_OPTIONS.map((s) => [s.key, s.label]));

// Dateiname „MARKE_MODELLCODE_nr.jpg" → { brand, model_code }
function parseFilename(name) {
    if (!name) return { brand: null, model_code: null };
    const base = name.replace(/\.[A-Za-z0-9]{2,5}$/, "");
    const parts = base.split(/[_\s]+/).filter(Boolean);
    if (!parts.length) return { brand: null, model_code: null };
    let code = null;
    if (parts.length >= 2) {
        let codeParts = parts.slice(1);
        if (codeParts.length > 1 && /^\d+$/.test(codeParts[codeParts.length - 1])) codeParts = codeParts.slice(0, -1);
        code = codeParts.join(" ") || null;
    }
    return { brand: parts[0], model_code: code };
}

const EMPTY = { brand: "", model_code: "", model_name: "", category: "motorcycle", conditions: ["neu", "tageszulassung"], image_urls: [], is_orientation_only: false, shared_scope: "none", copyright_consent: false };

export default function DealerVehicleImagesPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], counts: {} });
    const [queue, setQueue] = useState({ items: [], count: 0 });
    const [busy, setBusy] = useState(true);
    const [statusFilter, setStatusFilter] = useState("approved");
    const [q, setQ] = useState("");
    const [form, setForm] = useState(null);
    const [guideOpen, setGuideOpen] = useState(false);
    const [view, setView] = useState("own"); // own | shared
    const [shared, setShared] = useState({ items: [], count: 0 });
    const [companyBg, setCompanyBg] = useState(null);
    const [bgUploading, setBgUploading] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = { status: statusFilter };
            if (q) params.q = q;
            const [lib, rq] = await Promise.all([
                api.get("/dealer/vehicle-images", { params }),
                api.get("/dealer/vehicle-images/review-queue"),
            ]);
            setData(lib.data);
            setQueue(rq.data);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, [statusFilter, q]);

    const loadShared = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get("/dealer/vehicle-images/shared", { params: q ? { q } : {} });
            setShared(r.data);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, [q]);

    useEffect(() => {
        if (loading) return;
        if (!user || !["b2b", "admin"].includes(user.role)) { navigate("/"); return; }
        api.get("/users/me/dealer-profile").then((r) => setCompanyBg(r.data?.company_background_url || null)).catch(() => {});
        if (view === "shared") loadShared(); else load();
    }, [user, loading, navigate, load, loadShared, view]);

    const onCompanyBg = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBgUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/dealer/vehicle-images/company-background", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCompanyBg(r.data.url);
            toast.success("Firmen-Hintergrund gespeichert");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setBgUploading(false); e.target.value = ""; }
    };
    const removeCompanyBg = async () => {
        try {
            await api.patch("/users/me/dealer-profile", { company_background_url: "" });
            setCompanyBg(null);
            toast.success("Firmen-Hintergrund entfernt");
        } catch { toast.error("Fehler"); }
    };
    const importShared = async (id) => {
        try { await api.post(`/dealer/vehicle-images/${id}/import-shared`); toast.success("In eigene Bibliothek übernommen"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    };

    const resolve = async (id, action) => {
        await api.post(`/dealer/vehicle-images/${id}/resolve`, { action });
        toast.success(action === "library" ? "In Bibliothek übernommen" : action === "vehicle_only" ? "Nur am Fahrzeug belassen" : "Verworfen");
        load();
    };
    const del = async (id) => { await api.delete(`/dealer/vehicle-images/${id}`); load(); };
    const archive = async (id) => { await api.patch(`/dealer/vehicle-images/${id}`, { status: "archived" }); load(); };

    if (loading || !user) return <div className="p-12">Lädt…</div>;
    const counts = data.counts || {};

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="bilddatenbank-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                        <Images className="w-7 h-7 text-[#FFCC00]" /> Bilddatenbank
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Modell-generische Fahrzeugbilder. <strong>Neuwagen & Tageszulassungen teilen</strong> sich die Bilder (Marke + Modellcode). Vorführ-/Gebrauchtfahrzeuge bleiben am Fahrzeug — in der Bibliothek nur als <strong>Symbolfoto</strong>.
                    </p>
                </div>
                <button onClick={() => setForm({ ...EMPTY })} className="inline-flex items-center gap-2 bg-[#111827] text-white rounded-sm px-4 py-2.5 text-sm font-bold hover:bg-black" data-testid="bilddb-add-btn">
                    <Plus className="w-4 h-4" /> Bild hinzufügen
                </button>
            </div>

            <div className="mt-4 border border-gray-200 rounded-sm bg-white" data-testid="bilddb-guide">
                <button onClick={() => setGuideOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="bilddb-guide-toggle">
                    <span className="inline-flex items-center gap-2 font-bold text-sm text-[#111827]"><Info className="w-4 h-4 text-[#FFCC00]" /> Anleitung: So werden Bilder den Fahrzeugen zugeordnet</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
                </button>
                {guideOpen && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 text-sm text-gray-600 space-y-3">
                        <p>Die Zuordnung erfolgt automatisch über <strong>Marke</strong> + <strong>Modellcode</strong> (Hersteller-Code). Stimmen diese mit einem Neuwagen oder einer Tageszulassung überein, wird das Bild dort angezeigt.</p>
                        <div>
                            <div className="font-bold text-[#111827] mb-1">Pflichtangaben je Bild</div>
                            <ul className="list-disc pl-5 space-y-1">
                                <li><strong>Marke</strong> – exakt wie am Fahrzeug, z. B. <code className="bg-gray-100 px-1 rounded">BMW</code>, <code className="bg-gray-100 px-1 rounded">Vespa</code>.</li>
                                <li><strong>Modellcode</strong> (empfohlen) – der Hersteller-Code, z. B. <code className="bg-gray-100 px-1 rounded">K21</code>. Alternativ ein <strong>Modellname</strong>.</li>
                                <li><strong>Kategorie</strong> – Motorrad oder Fahrrad.</li>
                                <li><strong>Zustände</strong> – „neu" und/oder „Tageszulassung" (diese teilen sich die Bilder).</li>
                            </ul>
                        </div>
                        <div>
                            <div className="font-bold text-[#111827] mb-1">Empfohlene Dateibenennung</div>
                            <p className="mb-1">Damit du Bilder schnell zuordnen kannst, benenne die Dateien nach dem Schema:</p>
                            <code className="block bg-gray-100 rounded p-2 text-[13px]">MARKE_MODELLCODE_laufnummer.jpg</code>
                            <p className="mt-1 text-[13px] text-gray-500">Beispiele: <code className="bg-gray-100 px-1 rounded">BMW_K21_01.jpg</code>, <code className="bg-gray-100 px-1 rounded">Vespa_GTS300_03.jpg</code> — Marke und Modellcode mit Unterstrich getrennt, fortlaufende Nummer am Ende.</p>
                        </div>
                        <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <p className="text-[13px]"><strong>Vorführ- & Gebrauchtfahrzeuge:</strong> Bilder bleiben am einzelnen Fahrzeug. In der Bibliothek dienen sie nur als <strong>Symbolfoto</strong> und werden automatisch entsprechend gekennzeichnet.</p>
                        </div>
                        <p className="text-[13px] text-gray-500">Hinweis: Neue Bilder durchlaufen die <strong>Prüfung</strong>, bevor sie <strong>freigegeben</strong> und an den Fahrzeugen sichtbar werden.</p>
                    </div>
                )}
            </div>

            <div className="mt-4 border border-gray-200 rounded-sm bg-white p-4 flex items-center gap-4 flex-wrap" data-testid="bilddb-company-bg">
                <div className="w-24 h-16 bg-gray-100 rounded-sm overflow-hidden flex items-center justify-center shrink-0 border">
                    {companyBg
                        ? <img src={companyBg} alt="Firmen-Hintergrund" className="w-full h-full object-cover" data-testid="bilddb-company-bg-preview" />
                        : <Building2 className="w-7 h-7 text-gray-300" />}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#111827] flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#FFCC00]" /> Firmen-Hintergrund (KI)</div>
                    <p className="text-xs text-gray-500 mt-0.5">Standard-Hintergrund für die KI-Optimierung „Firmen-Hintergrund". {companyBg ? "Hinterlegt." : "Noch nicht hinterlegt."}</p>
                </div>
                <div className="flex gap-2">
                    <label className="inline-flex items-center gap-1.5 border border-gray-300 rounded-sm px-3 py-1.5 text-xs font-bold cursor-pointer hover:border-[#FFCC00]" data-testid="bilddb-company-bg-upload-label">
                        {bgUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {companyBg ? "Ersetzen" : "Hochladen"}
                        <input type="file" accept="image/*" className="hidden" onChange={onCompanyBg} data-testid="bilddb-company-bg-upload" />
                    </label>
                    {companyBg && <button onClick={removeCompanyBg} className="text-xs font-bold text-red-600 border border-red-200 rounded-sm px-3 py-1.5 hover:bg-red-50" data-testid="bilddb-company-bg-remove">Entfernen</button>}
                </div>
            </div>

            <div className="flex gap-2 mt-5" data-testid="bilddb-view-toggle">
                <button onClick={() => setView("own")} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border ${view === "own" ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid="bilddb-view-own">Meine Bibliothek</button>
                <button onClick={() => setView("shared")} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${view === "shared" ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid="bilddb-view-shared"><Share2 className="w-3.5 h-3.5" /> Geteilt mit mir</button>
            </div>

            {view === "own" && queue.count > 0 && (
                <div className="mt-5 border border-amber-300 bg-amber-50 rounded-sm p-4" data-testid="bilddb-review-queue">
                    <div className="flex items-center gap-2 font-bold text-amber-900 mb-3">
                        <AlertTriangle className="w-5 h-5" /> {queue.count} Bild(er) zur Prüfung
                    </div>
                    <div className="space-y-2">
                        {queue.items.map((it) => (
                            <div key={it.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2 flex-wrap" data-testid={`bilddb-queue-${it.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    {it.image_urls?.[0] && <img src={it.image_urls[0]} alt="" className="w-12 h-12 object-cover rounded-sm border" />}
                                    <div className="text-sm">
                                        <div className="font-bold text-[#111827]">{it.brand} {it.model_name || it.model_code}</div>
                                        <div className="text-xs text-gray-500">{(it.conditions || []).map((c) => COND_LABEL[c] || c).join(", ")}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => resolve(it.id, "library")} className="text-xs font-bold bg-emerald-600 text-white rounded-sm px-2.5 py-1.5 hover:bg-emerald-700" data-testid={`bilddb-queue-library-${it.id}`}>Übernehmen</button>
                                    <button onClick={() => resolve(it.id, "vehicle_only")} className="text-xs font-bold border border-gray-300 rounded-sm px-2.5 py-1.5 hover:bg-gray-50" data-testid={`bilddb-queue-vehicle-${it.id}`}>Nur am Fahrzeug</button>
                                    <button onClick={() => resolve(it.id, "discard")} className="text-xs font-bold text-red-600 border border-red-200 rounded-sm px-2.5 py-1.5 hover:bg-red-50" data-testid={`bilddb-queue-discard-${it.id}`}>Verwerfen</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {view === "own" ? (<>
            <div className="flex flex-wrap gap-2 mt-6 mb-4 items-center">
                {["approved", "review", "archived"].map((s) => (
                    <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border ${statusFilter === s ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`bilddb-filter-${s}`}>
                        {STATUS_LABEL[s]} ({counts[s] ?? 0})
                    </button>
                ))}
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (Marke/Modell/Code)" className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm flex-1 min-w-[200px]" data-testid="bilddb-search" />
            </div>

            {busy ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : data.items.length === 0 ? (
                <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="bilddb-empty">Keine Einträge.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bilddb-grid">
                    {data.items.map((it) => (
                        <div key={it.id} className="border border-gray-200 rounded-sm overflow-hidden bg-white" data-testid={`bilddb-card-${it.id}`}>
                            <div className="aspect-video bg-gray-100 relative">
                                {it.image_urls?.[0]
                                    ? <img src={it.image_urls[0]} alt={it.model_name} className="w-full h-full object-cover" />
                                    : <div className="w-full h-full flex items-center justify-center text-gray-300"><Bike className="w-10 h-10" /></div>}
                                {it.is_orientation_only && (
                                    <span className="absolute bottom-1 left-1 text-[10px] font-bold uppercase bg-black/70 text-white px-1.5 py-0.5 rounded-sm" data-testid={`bilddb-symbol-${it.id}`}>Symbolfoto</span>
                                )}
                                {it.shared_scope && it.shared_scope !== "none" && (
                                    <span className="absolute top-1 left-1 text-[10px] font-bold uppercase bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1" data-testid={`bilddb-shared-badge-${it.id}`}><Share2 className="w-3 h-3" /> {SHARE_LABEL[it.shared_scope]}</span>
                                )}
                                {it.image_urls?.length > 1 && <span className="absolute top-1 right-1 text-[10px] font-bold bg-white/90 px-1.5 py-0.5 rounded-sm">+{it.image_urls.length - 1}</span>}
                            </div>
                            <div className="p-3">
                                <div className="font-black text-[#111827] truncate">{it.brand} {it.model_name || ""}</div>
                                {it.model_code && <div className="text-xs text-gray-500 font-mono-num">Code: {it.model_code}</div>}
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {(it.conditions || []).map((c) => (
                                        <span key={c} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border ${["neu", "tageszulassung"].includes(c) ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-gray-100 text-gray-600 border-gray-300"}`}>{COND_LABEL[c] || c}</span>
                                    ))}
                                </div>
                                <div className="flex gap-2 mt-3">
                                    {it.status !== "archived" && <button onClick={() => archive(it.id)} className="text-xs inline-flex items-center gap-1 text-gray-600 hover:text-gray-900" data-testid={`bilddb-archive-${it.id}`}><Archive className="w-3.5 h-3.5" /> Archivieren</button>}
                                    <button onClick={() => del(it.id)} className="text-xs inline-flex items-center gap-1 text-red-600 hover:text-red-800 ml-auto" data-testid={`bilddb-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /> Löschen</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </>) : (
                <div className="mt-6">
                    <div className="mb-4">
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (Marke/Modell/Code)" className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm w-full max-w-md" data-testid="bilddb-shared-search" />
                    </div>
                    {busy ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                    ) : shared.items.length === 0 ? (
                        <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="bilddb-shared-empty">Keine geteilten Bilder verfügbar.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bilddb-shared-grid">
                            {shared.items.map((it) => (
                                <div key={it.id} className="border border-gray-200 rounded-sm overflow-hidden bg-white" data-testid={`bilddb-shared-card-${it.id}`}>
                                    <div className="aspect-video bg-gray-100 relative">
                                        {it.image_urls?.[0]
                                            ? <img src={it.image_urls[0]} alt={it.model_name} className="w-full h-full object-cover" />
                                            : <div className="w-full h-full flex items-center justify-center text-gray-300"><Bike className="w-10 h-10" /></div>}
                                        <span className="absolute top-1 left-1 text-[10px] font-bold uppercase bg-black/70 text-white px-1.5 py-0.5 rounded-sm">{it.from_org ? "Filiale" : "Händler"}</span>
                                    </div>
                                    <div className="p-3">
                                        <div className="font-black text-[#111827] truncate">{it.brand} {it.model_name || ""}</div>
                                        {it.model_code && <div className="text-xs text-gray-500 font-mono-num">Code: {it.model_code}</div>}
                                        <div className="text-xs text-gray-500 mt-1 truncate">von {it.dealer_name}</div>
                                        <button onClick={() => importShared(it.id)} className="mt-3 w-full text-xs font-bold bg-[#111827] text-white rounded-sm px-3 py-2 hover:bg-black inline-flex items-center justify-center gap-1.5" data-testid={`bilddb-shared-import-${it.id}`}><Download className="w-3.5 h-3.5" /> In meine Bibliothek</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {form && <AddDialog form={form} setForm={setForm} companyBg={companyBg} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
        </div>
    );
}

const AI_MODE_OPTIONS = [
    { key: "none", label: "Original", icon: Images },
    { key: "freistellen", label: "Freistellen (weiß)", icon: Eraser },
    { key: "firmen", label: "Firmen-HG", icon: Building2 },
    { key: "twowheels_neu", label: "TwoWheels Neu", icon: Sparkles },
    { key: "twowheels_gebraucht", label: "TwoWheels Gebraucht", icon: Sparkles },
];

function AddDialog({ form, setForm, companyBg, onClose, onDone }) {
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showMedia, setShowMedia] = useState(false);
    const [meta, setMeta] = useState([]); // parallel zu form.image_urls: {original, mode, busy}
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const toggleCond = (key) => setForm((f) => ({
        ...f, conditions: f.conditions.includes(key) ? f.conditions.filter((c) => c !== key) : [...f.conditions, key],
    }));

    const addImages = (urls) => {
        setForm((f) => ({ ...f, image_urls: [...f.image_urls, ...urls] }));
        setMeta((m) => [...m, ...urls.map((u) => ({ original: u, mode: "none", busy: false }))]);
    };
    const removeImage = (i) => {
        setForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, j) => j !== i) }));
        setMeta((m) => m.filter((_, j) => j !== i));
    };

    const onFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const r = await api.post("/dealer/vehicle-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                addImages([r.data.url]);
                // Marke/Modellcode aus Dateiname vorbefüllen (nur wenn leer)
                const parsed = r.data.parsed || parseFilename(file.name);
                setForm((f) => ({
                    ...f,
                    brand: f.brand?.trim() ? f.brand : (parsed.brand || ""),
                    model_code: f.model_code?.trim() ? f.model_code : (parsed.model_code || ""),
                }));
            }
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const applyAi = async (i, mode, silent = false) => {
        const orig = meta[i]?.original || form.image_urls[i];
        if (mode === "none") {
            setForm((f) => { const arr = [...f.image_urls]; arr[i] = orig; return { ...f, image_urls: arr }; });
            setMeta((m) => m.map((x, j) => j === i ? { ...x, mode: "none" } : x));
            return true;
        }
        if (mode === "firmen" && !companyBg) { toast.error("Bitte zuerst einen Firmen-Hintergrund hinterlegen."); return false; }
        setMeta((m) => m.map((x, j) => j === i ? { ...x, busy: true } : x));
        try {
            const r = await api.post("/dealer/vehicle-images/ai-process", { image_url: orig, mode });
            setForm((f) => { const arr = [...f.image_urls]; arr[i] = r.data.url; return { ...f, image_urls: arr }; });
            setMeta((m) => m.map((x, j) => j === i ? { ...x, mode, busy: false } : x));
            if (!silent) toast.success("KI-Bild erstellt (kostenpflichtig, siehe Kosten & Nutzung)");
            return true;
        } catch (e) {
            setMeta((m) => m.map((x, j) => j === i ? { ...x, busy: false } : x));
            toast.error(e?.response?.data?.detail || "KI-Bearbeitung fehlgeschlagen");
            return false;
        }
    };

    // Stapel-Verarbeitung: einen KI-Modus auf alle Bilder anwenden (spart Zeit bei großen Beständen).
    const [batchMode, setBatchMode] = useState("freistellen");
    const [batchBusy, setBatchBusy] = useState(false);
    const applyAiAll = async () => {
        if (!form.image_urls.length) { toast.error("Keine Bilder vorhanden"); return; }
        if (batchMode === "firmen" && !companyBg) { toast.error("Bitte zuerst einen Firmen-Hintergrund hinterlegen."); return; }
        setBatchBusy(true);
        let ok = 0;
        for (let i = 0; i < form.image_urls.length; i++) {
            const res = await applyAi(i, batchMode, true);
            if (res || batchMode === "none") ok += 1;
        }
        setBatchBusy(false);
        toast.success(`Stapel-Verarbeitung fertig: ${ok}/${form.image_urls.length} Bilder${batchMode !== "none" ? " (kostenpflichtig, siehe Kosten & Nutzung)" : ""}`);
    };

    const save = async () => {
        if (!form.brand?.trim()) { toast.error("Marke erforderlich"); return; }
        if (!form.model_code?.trim() && !form.model_name?.trim()) { toast.error("Modellcode oder Modellname erforderlich"); return; }
        if (!form.image_urls.length) { toast.error("Mindestens ein Bild hochladen"); return; }
        if (form.shared_scope !== "none" && !form.copyright_consent) { toast.error("Bitte die Bildrechte-Einwilligung bestätigen, um zu teilen."); return; }
        setSaving(true);
        try {
            await api.post("/dealer/vehicle-images", { ...form, source: "manual", status: "approved" });
            toast.success("Gespeichert");
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setSaving(false); }
    };

    const hasSeparate = form.conditions.some((c) => ["vorfuehr", "gebraucht"].includes(c));

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="bilddb-add-dialog">
            <div className="bg-white rounded-sm max-w-lg w-full max-h-[88vh] overflow-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h2 className="font-black text-lg">Bild hinzufügen</h2>
                    <button onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <input placeholder="Marke *" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="bilddb-form-brand" />
                        <select value={form.category} onChange={(e) => set("category", e.target.value)} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="bilddb-form-category">
                            <option value="motorcycle">Motorrad</option>
                            <option value="bicycle">Fahrrad</option>
                        </select>
                        <input placeholder="Modellcode (Hersteller)" value={form.model_code} onChange={(e) => set("model_code", e.target.value)} className="border border-gray-300 rounded-sm p-2 text-sm font-mono-num" data-testid="bilddb-form-code" />
                        <input placeholder="Modellname" value={form.model_name} onChange={(e) => set("model_name", e.target.value)} className="border border-gray-300 rounded-sm p-2 text-sm" data-testid="bilddb-form-model" />
                    </div>
                    <div>
                        <div className="text-xs font-bold text-gray-600 mb-1.5 flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Gültig für Zustand</div>
                        <div className="flex flex-wrap gap-2">
                            {COND_OPTIONS.map((c) => (
                                <button key={c.key} onClick={() => toggleCond(c.key)} className={`text-xs font-bold px-2.5 py-1.5 rounded-sm border ${form.conditions.includes(c.key) ? (c.shared ? "bg-blue-600 text-white border-blue-600" : "bg-gray-700 text-white border-gray-700") : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`bilddb-form-cond-${c.key}`}>
                                    {c.label}
                                </button>
                            ))}
                        </div>
                        {hasSeparate && (
                            <div className="text-[11px] text-amber-700 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Vorführ/Gebraucht werden automatisch als <strong className="mx-1">Symbolfoto</strong> gekennzeichnet.</div>
                        )}
                    </div>
                    <div>
                        <label className="inline-flex items-center gap-2 border border-dashed border-gray-300 rounded-sm px-3 py-2 text-sm cursor-pointer hover:border-[#FFCC00]" data-testid="bilddb-form-upload-label">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Bilder hochladen
                            <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} data-testid="bilddb-form-upload" />
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowMedia(true)}
                            className="inline-flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2 text-sm ml-2 hover:border-[#111827]"
                            data-testid="bilddb-form-media-btn"
                        >
                            <FolderOpen className="w-4 h-4" /> Aus Medienablage wählen
                        </button>
                        {form.image_urls.length > 0 && (
                            <div className="space-y-3 mt-3" data-testid="bilddb-form-images">
                                {form.image_urls.length > 1 && (
                                    <div className="flex flex-wrap items-center gap-2 bg-[#FFF9E0] border border-[#FFE082] rounded-sm p-2" data-testid="bilddb-batch-bar">
                                        <span className="text-[11px] font-bold text-[#111827] inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#FFCC00]" /> Stapel-Verarbeitung ({form.image_urls.length} Bilder):</span>
                                        <select value={batchMode} onChange={(e) => setBatchMode(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 py-1 bg-white" data-testid="bilddb-batch-mode">
                                            {AI_MODE_OPTIONS.map((opt) => (
                                                <option key={opt.key} value={opt.key} disabled={opt.key === "firmen" && !companyBg}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <button type="button" disabled={batchBusy} onClick={applyAiAll} className="text-xs font-bold bg-[#111827] text-white rounded-sm px-3 py-1.5 hover:bg-black disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="bilddb-batch-apply">
                                            {batchBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Auf alle anwenden
                                        </button>
                                    </div>
                                )}
                                {form.image_urls.map((u, i) => (
                                    <div key={i} className="flex gap-3 border border-gray-200 rounded-sm p-2" data-testid={`bilddb-form-image-${i}`}>
                                        <div className="relative shrink-0">
                                            <img src={u} alt="" className="w-20 h-20 object-cover rounded-sm border" />
                                            {meta[i]?.busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-sm"><Loader2 className="w-5 h-5 animate-spin text-[#111827]" /></div>}
                                            <button onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-testid={`bilddb-form-image-remove-${i}`}><X className="w-3 h-3" /></button>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[11px] font-bold text-gray-500 flex items-center gap-1 mb-1"><Wand2 className="w-3.5 h-3.5 text-[#FFCC00]" /> KI-Hintergrund (kostenpflichtig)</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {AI_MODE_OPTIONS.map((opt) => {
                                                    const active = (meta[i]?.mode || "none") === opt.key;
                                                    const disabled = meta[i]?.busy || (opt.key === "firmen" && !companyBg);
                                                    return (
                                                        <button
                                                            key={opt.key}
                                                            type="button"
                                                            disabled={disabled}
                                                            onClick={() => applyAi(i, opt.key)}
                                                            title={opt.key === "firmen" && !companyBg ? "Kein Firmen-Hintergrund hinterlegt" : opt.label}
                                                            className={`text-[11px] font-bold px-2 py-1 rounded-sm border inline-flex items-center gap-1 disabled:opacity-40 ${active ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                                                            data-testid={`bilddb-ai-${opt.key}-${i}`}
                                                        >
                                                            <opt.icon className="w-3 h-3" /> {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs font-bold text-gray-600 mb-1.5 flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Mit anderen teilen</div>
                        <div className="flex flex-wrap gap-2">
                            {SHARE_OPTIONS.map((s) => (
                                <button key={s.key} type="button" onClick={() => set("shared_scope", s.key)} className={`text-xs font-bold px-2.5 py-1.5 rounded-sm border ${form.shared_scope === s.key ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`bilddb-form-share-${s.key}`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        {form.shared_scope !== "none" && (
                            <label className="flex items-start gap-2 mt-2 text-[12px] text-gray-700 cursor-pointer" data-testid="bilddb-form-consent-label">
                                <input type="checkbox" checked={!!form.copyright_consent} onChange={(e) => set("copyright_consent", e.target.checked)} className="mt-0.5" data-testid="bilddb-form-consent" />
                                <span>Ich bestätige, dass ich die <strong>Bildrechte</strong> besitze bzw. die nötige Freigabe habe, um diese Bilder mit anderen Händlern/Filialen zu teilen.</span>
                            </label>
                        )}
                    </div>
                </div>
                <div className="px-5 pb-5">
                    <button onClick={save} disabled={saving || uploading} className="w-full bg-[#111827] text-white rounded-sm py-2.5 font-bold hover:bg-black disabled:opacity-60" data-testid="bilddb-form-save">
                        {saving ? "Speichert…" : "In Bibliothek speichern"}
                    </button>
                </div>
            </div>
            {showMedia && (
                <MediaPickerModal
                    selectedUrls={form.image_urls}
                    onClose={() => setShowMedia(false)}
                    onConfirm={(urls) => {
                        const existing = new Set(form.image_urls);
                        addImages(urls.filter((u) => !existing.has(u)));
                        setShowMedia(false);
                    }}
                />
            )}
        </div>
    );
}

function MediaPickerModal({ selectedUrls = [], onClose, onConfirm }) {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");
    const [picked, setPicked] = useState(new Set());

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            api.get("/dealer/media-files", { params: q ? { q } : {} })
                .then((r) => { if (alive) setItems(r.data?.items || []); })
                .catch(() => { if (alive) setItems([]); });
        }, 250);
        return () => { alive = false; clearTimeout(t); };
    }, [q]);

    const toggle = (url) => setPicked((p) => {
        const next = new Set(p);
        if (next.has(url)) next.delete(url); else next.add(url);
        return next;
    });

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="bilddb-media-modal">
            <div className="bg-white rounded-sm max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h2 className="font-black text-lg flex items-center gap-2"><FolderOpen className="w-5 h-5 text-[#FFCC00]" /> Medienablage</h2>
                    <button onClick={onClose} data-testid="bilddb-media-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-5 py-3 border-b border-gray-100">
                    <div className="relative">
                        <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dateiname suchen…" className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm" data-testid="bilddb-media-search" />
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-5">
                    {items === null ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center text-gray-500 py-12 border border-dashed border-gray-300 rounded-sm" data-testid="bilddb-media-empty">Keine Bilddateien in der Medienablage.</div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="bilddb-media-grid">
                            {items.map((it) => {
                                const already = selectedUrls.includes(it.url);
                                const on = picked.has(it.url);
                                return (
                                    <button
                                        key={it.id}
                                        type="button"
                                        onClick={() => !already && toggle(it.url)}
                                        className={`relative aspect-square rounded-sm overflow-hidden border-2 transition-colors ${already ? "border-gray-200 opacity-50 cursor-default" : on ? "border-[#FFCC00]" : "border-transparent hover:border-gray-300"}`}
                                        title={it.name}
                                        data-testid={`bilddb-media-item-${it.id}`}
                                    >
                                        <img src={it.url} alt={it.name} className="w-full h-full object-cover" />
                                        {(on || already) && (
                                            <span className="absolute top-1 right-1 bg-[#111827] text-[#FFCC00] rounded-full p-0.5"><CheckCircle2 className="w-4 h-4" /></span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-500">{picked.size} ausgewählt</span>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-bold border border-gray-300 rounded-sm hover:bg-gray-50" data-testid="bilddb-media-cancel">Abbrechen</button>
                        <button onClick={() => onConfirm(Array.from(picked))} disabled={picked.size === 0} className="px-4 py-2 text-sm font-bold bg-[#111827] text-white rounded-sm hover:bg-black disabled:opacity-50" data-testid="bilddb-media-confirm">Übernehmen</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
